/** Shared stream/retry/wallet state machine; adapters supply the context and HTTP contract. */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { streamChat, type ChatErrorCode } from '../lib/chat-sse';
import {
  GONE, NOT_STORED, countUsers, fetchStoredThread, fetchWalletBalance, isAnswered, loadErrorOf,
  RECONCILE_POLL_MS, RECONCILE_WAIT_MS, STAR_CREDIT_POLL_MS, STAR_CREDIT_WAIT_MS,
  type ChatHeader, type ChatMsg,
} from '../lib/chat-sync';
import type { UseAstrologerChat } from './useAstrologerChat';
import type { CourseChatPreview } from '../types/course';
const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
export interface SessionCourse {
  preview: CourseChatPreview | null;
  storageKey: string | undefined;
  loadStored: (id: string, signal: AbortSignal) => Promise<ChatMsg[] | null>;
  refreshPreview: () => void;
}
interface SessionOptions {
  targetKey: string; base: ChatMsg[] | null; loadedHeader: ChatHeader | null;
  knownChartId: string | null; chartId: string | null; threadId: string | null;
  isNew: boolean; query: { isError: boolean; error: unknown; refetch: () => unknown };
  jwt: string | null; lang: string; surface?: string; seedRef?: string | null; course?: SessionCourse;
}
interface Pending { requote?: boolean; text: string; requestId: string; body: unknown; path: string; conversationId: string | null }
function readPending(key: string | undefined): Pending | null {
  try { return key ? JSON.parse(sessionStorage.getItem(key) ?? 'null') as Pending | null : null; } catch { return null; }
}
export function useChatSession({ targetKey, base, loadedHeader, knownChartId, chartId, threadId, isNew, query, jwt, lang, surface, seedRef, course }: SessionOptions): UseAstrologerChat & { open: () => void; resume: () => boolean } {
  const qc = useQueryClient();
  // Once the user sends a message the conversation is frozen here (a refetch never rewrites it).
  const [conv, setConv] = useState<{ messages: ChatMsg[]; header: ChatHeader | null } | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<ChatErrorCode | null>(null);
  const [needTopUp, setNeedTopUpState] = useState(false);
  const [waitingForStars, setWaitingForStars] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(threadId ?? readPending(course?.storageKey)?.conversationId ?? null);

  const convIdRef = useRef<string | null>(threadId ?? readPending(course?.storageKey)?.conversationId ?? null);
  /** The chart of this conversation, for the unmount cleanup (the effect does not see render values). */
  const chartIdRef = useRef<string | null>(knownChartId);
  chartIdRef.current = knownChartId;
  const requestRef = useRef<Pending | null>(readPending(course?.storageKey));
  const sendSequence = useRef(0);
  if (threadId && !convIdRef.current) convIdRef.current = threadId;
  const pendingRef = useRef<string | null>(requestRef.current?.text ?? null);
  const persist = (): void => {
    const storageKey = course?.storageKey;
    if (!storageKey) return;
    try {
      if (requestRef.current) sessionStorage.setItem(storageKey, JSON.stringify(requestRef.current));
      else sessionStorage.removeItem(storageKey);
    } catch { /* Storage can be unavailable in a webview; in-memory retry remains safe. */ }
  };
  const clearPending = (): void => { pendingRef.current = null; requestRef.current = null; persist(); };
  const streamingRef = useRef(false);
  const openingFailedRef = useRef(false);
  const goneRef = useRef(false);
  const needTopUpRef = useRef(false);
  const genRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /** Threads whose cached history is stale after this screen sent into them. */
  const touchedRef = useRef(new Set<string>());
  /** `insufficient_stars` took the pending question off the screen (cases a/b). */
  const takenBackRef = useRef(false);
  /** Wallet balance when `insufficient_stars` opened the sheet (null = unknown). */
  const balanceBeforeRef = useRef<Promise<number | null> | null>(null);
  const waitingRef = useRef(false);
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  /** The last send error, for `retry` (network / in_flight → read the thread first). */
  const lastErrorRef = useRef<ChatErrorCode | null>(null);
  const retryRef = useRef<() => void>(() => undefined);
  /** POSTs the pending question again (latest render's doSend). */
  const retryPostRef = useRef<(text: string) => void>(() => undefined);

  // Another target in the same mounted screen (e.g. ?thread=a → ?thread=b): start over.
  const keyRef = useRef(targetKey);
  if (keyRef.current !== targetKey) {
    keyRef.current = targetKey;
    openingFailedRef.current = false;
    genRef.current += 1; // late callbacks of the old stream are ignored
    convIdRef.current = threadId;
    requestRef.current = readPending(course?.storageKey);
    pendingRef.current = requestRef.current?.text ?? null;
    streamingRef.current = false;
    goneRef.current = false;
    needTopUpRef.current = false;
    takenBackRef.current = false;
    balanceBeforeRef.current = null;
    waitingRef.current = false;
    lastErrorRef.current = null;
    setConv(null);
    setConversationId(threadId);
    setStreaming(false);
    setError(null);
    setNeedTopUpState(false);
    setWaitingForStars(false);
  }

  useEffect(() => {
    const touched = touchedRef.current;
    const timers = timersRef.current;
    return () => {
      genRef.current += 1;
      if (abortRef.current) {
        // Left in the middle of an answer: the server still finishes (and charges) it.
        abortRef.current.abort();
        abortRef.current = null;
        void qc.invalidateQueries({ queryKey: ['star-wallet'] });
        void qc.invalidateQueries({ queryKey: ['chat-threads'] });
        // Its conversation count (shown in the delete dialog) may have grown.
        if (chartIdRef.current) void qc.invalidateQueries({ queryKey: ['chart', chartIdRef.current] });
      }
      for (const t of timers) clearTimeout(t);
      timers.clear();
      waitingRef.current = false;
      streamingRef.current = false;
      for (const id of touched) {
        qc.removeQueries({ queryKey: ['chat-thread', id], exact: true });
        qc.removeQueries({ queryKey: ['course-chat-thread', id], exact: true });
      }
      touched.clear();
    };
  }, [targetKey, qc]);

  const loadState: UseAstrologerChat['loadState'] = conv || base ? 'ready' : query.isError ? 'error' : 'loading';
  const loadError = loadState === 'error' ? loadErrorOf(query.error, isNew ? 'new' : 'thread') : null;
  // The chart (or the conversation) is gone: «К моим картам» must not show it from the cache.
  const goneOnLoad = loadError === 'chart_not_found' || loadError === 'not_found';
  useEffect(() => {
    if (!goneOnLoad) return;
    void qc.invalidateQueries({ queryKey: ['charts'] });
    if (chartId) qc.removeQueries({ queryKey: ['chart', chartId] });
  }, [goneOnLoad, chartId, qc]);
  const messages = conv?.messages ?? base ?? [];
  const header = conv ? conv.header : loadedHeader;

  const setNeedTopUp = (v: boolean): void => {
    needTopUpRef.current = v;
    setNeedTopUpState(v);
  };
  const updateMessages = (f: (m: ChatMsg[]) => ChatMsg[]): void =>
    setConv((c) => (c ? { ...c, messages: f(c.messages) } : c));

  const fetchBalance = (): Promise<number | null> => fetchWalletBalance(jwt as string);
  const loadStored = (id: string, signal: AbortSignal): Promise<ChatMsg[] | null> =>
    course ? course.loadStored(id, signal) : fetchStoredThread(jwt as string, id, signal);

  /** A timer cleared on unmount / target change (the awaiting code then sees `live()` false or never resumes). */
  const sleep = (ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
      const t = setTimeout(() => {
        timersRef.current.delete(t);
        resolve();
      }, ms);
      timersRef.current.add(t);
    });

  const refreshAfterAnswer = (invalidateChartId: string | null): void => {
    void qc.invalidateQueries({ queryKey: ['star-wallet'] });
    void qc.invalidateQueries({ queryKey: ['chat-threads'] });
    if (invalidateChartId) void qc.invalidateQueries({ queryKey: ['chart', invalidateChartId] });
  };

  /** The server has the answer: show its history, nothing pending, no error. */
  const showStored = (stored: ChatMsg[], invalidateChartId: string | null): void => {
    setConv((c) => ({ header: c?.header ?? loadedHeader, messages: stored }));
    clearPending();
    lastErrorRef.current = null;
    setError(null);
    refreshAfterAnswer(invalidateChartId);
  };

  const answered = (stored: ChatMsg[], localUsers: number): boolean => course
    ? !!requestRef.current && stored.some((m) => m.role === 'assistant' && m.requestId === requestRef.current?.requestId)
    : isAnswered(stored, localUsers);

  /** Re-reads the thread until it holds the answer (→ its history) or the wait is over (→ null). */
  const waitForStoredAnswer = async (
    id: string, localUsers: number, signal: AbortSignal, live: () => boolean,
  ): Promise<ChatMsg[] | null> => {
    const started = Date.now();
    for (;;) {
      const stored = await loadStored(id, signal);
      if (!live()) return null;
      if (stored && answered(stored, localUsers)) return stored;
      if (Date.now() - started >= RECONCILE_WAIT_MS) return null;
      await sleep(RECONCILE_POLL_MS);
      if (!live()) return null;
    }
  };

  const doSend = (raw: string, dropUnanswered: boolean): void => {
    const text = raw.trim();
    if (course && (!course.preview?.enabled || !convIdRef.current)) return;
    if (!text || !jwt || streamingRef.current || goneRef.current || waitingRef.current || loadState !== 'ready') return;

    const sequence = ++sendSequence.current;
    const seed = { messages: base ?? [], header: loadedHeader };
    // A retry replaces its own (last) bubble; questions the server never stored leave the screen.
    const clean = (msgs: ChatMsg[]): ChatMsg[] => {
      const out = dropUnanswered && msgs.at(-1)?.role === 'user' ? msgs.slice(0, -1) : msgs;
      return out.some((m) => m.unsent) ? out.filter((m) => !m.unsent) : out;
    };
    const localUsers = countUsers(clean(conv?.messages ?? seed.messages)) + 1; // stored questions, this one included
    setConv((c) => {
      const cur = c ?? seed;
      return { ...cur, messages: [...clean(cur.messages), { role: 'user' as const, content: text }, { role: 'assistant', content: '' }] };
    });
    pendingRef.current = text;
    takenBackRef.current = false;
    lastErrorRef.current = null;
    streamingRef.current = true;
    setStreaming(true);
    setError(null);

    const gen = genRef.current;
    const live = (): boolean => gen === genRef.current;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const conversationId = convIdRef.current;
    if (conversationId) touchedRef.current.add(conversationId);
    const quote = course?.preview;
    if (course && (!dropUnanswered || !requestRef.current || requestRef.current.requote)) {
      const requestId = crypto.randomUUID();
      const body = { conversationId, text, expectedVersion: quote!.version, expectedPriceStars: quote!.priceStars, requestId, lang };
      requestRef.current = { text, body, requestId, path: '/api/v1/course/chat/message', conversationId };
      persist();
    }
    const body = course ? requestRef.current!.body : conversationId
      ? { conversationId, text, lang }
      : { text, lang, chartId: chartId ?? undefined, surface: surface || undefined, seedRef: seedRef ?? undefined };
    const invalidateChartId = knownChartId;
    let gotText = false;
    const failure: { code: ChatErrorCode | null } = { code: null };

    const showError = (code: ChatErrorCode, unsent: boolean): void => {
      const takeBack = code === 'insufficient_stars' && !gotText;
      updateMessages((m) => {
        let out = m;
        const last = out.at(-1);
        if (last?.role === 'assistant' && last.content === '') out = out.slice(0, -1);
        const q = out.at(-1);
        if (takeBack && q?.role === 'user') out = out.slice(0, -1);
        else if (unsent && q?.role === 'user') out = [...out.slice(0, -1), { ...q, unsent: true }];
        return out;
      });
      if (code === 'insufficient_stars') {
        if (takeBack) {
          takenBackRef.current = true;
          balanceBeforeRef.current = fetchBalance().then(
            (b) => b ?? qc.getQueryData<{ balance?: number }>(['star-wallet'])?.balance ?? null,
          );
        } else {
          // With answer text the answer is stored (not charged): keep it and resend nothing.
          clearPending();
          void qc.invalidateQueries({ queryKey: ['chat-threads'] });
        }
        setNeedTopUp(true);
      } else if (code === 'quote_changed' || code === 'answer_changed' || code === 'version_conflict') {
        if (requestRef.current) { requestRef.current.requote = true; persist(); }
        course?.refreshPreview();
        setError(code);
      } else if (GONE.has(code)) {
        goneRef.current = true;
        clearPending();
        setError(code);
        // «К моим картам» must not show the deleted chart from the cache.
        void qc.invalidateQueries({ queryKey: ['charts'] });
        if (invalidateChartId) qc.removeQueries({ queryKey: ['chart', invalidateChartId] });
      } else {
        if (code === 'stream_error' || (code === 'network' && convIdRef.current)) {
          void qc.invalidateQueries({ queryKey: ['chat-threads'] }); // the question may be stored
        }
        lastErrorRef.current = code;
        setError(code);
      }
    };

    void (async () => {
      await streamChat({
        base: API_BASE,
        path: course ? requestRef.current!.path : undefined,
        jwt,
        body,
        signal: ctrl.signal,
        onConversation: (id) => {
          if (!live()) return;
          convIdRef.current = id;
          setConversationId(id);
          if (requestRef.current) { requestRef.current.conversationId = id; persist(); }
        },
        onDelta: (d) => {
          if (!live()) return;
          gotText = true;
          updateMessages((m) => {
            const last = m.at(-1);
            if (last?.role !== 'assistant') return m;
            return [...m.slice(0, -1), { role: 'assistant', content: last.content + d }];
          });
        },
        onDone: (d) => {
          if (!live()) return;
          if (d.conversationId) {
            convIdRef.current = d.conversationId;
            setConversationId(d.conversationId);
            if (requestRef.current) { requestRef.current.conversationId = d.conversationId; persist(); }
          }
          if (convIdRef.current) touchedRef.current.add(convIdRef.current);
          if (course && convIdRef.current) {
            void loadStored(convIdRef.current, ctrl.signal).then((stored) => {
              if (live() && sequence === sendSequence.current && stored) setConv((c) => c ? { ...c, messages: stored } : c);
            });
          }
          clearPending();
          setError(null);
          refreshAfterAnswer(invalidateChartId);
        },
        onError: (code) => {
          if (live()) failure.code = code;
        },
      });
      if (!live()) return;
      if (convIdRef.current) touchedRef.current.add(convIdRef.current);

      let code = failure.code;
      let unsent = code !== null && NOT_STORED.has(code) && !(dropUnanswered && code === 'in_flight'); // a retry's in_flight: the first attempt may have stored it
      const id = convIdRef.current;
      if (code === 'network' && id) {
        // The server finishes (and charges) the answer even when we dropped: wait for it before offering a retry.
        const stored = await waitForStoredAnswer(id, localUsers, ctrl.signal, live);
        if (!live()) return;
        if (stored) {
          showStored(stored, invalidateChartId);
          code = null;
        }
      } else if (code === 'stream_error' && id) {
        // The question may or may not be stored — and the answer too (e.g. the charge failed after it): look once.
        const stored = await loadStored(id, ctrl.signal);
        if (!live()) return;
        if (stored && answered(stored, localUsers)) {
          showStored(stored, invalidateChartId);
          code = null;
        } else if (stored && countUsers(stored) < localUsers) {
          unsent = true;
        }
      }
      if (code) showError(code, unsent);

      if (abortRef.current === ctrl) abortRef.current = null;
      streamingRef.current = false;
      setStreaming(false);
    })();
  };

  const retry = (): void => {
    const text = pendingRef.current;
    if (course && !text && openingFailedRef.current) { open(); return; }
    if (!text || streamingRef.current || goneRef.current || waitingRef.current) return;
    const id = convIdRef.current;
    const lastError = lastErrorRef.current;
    if (!id || (!course && lastError !== 'network' && lastError !== 'in_flight')) {
      doSend(text, true);
      return;
    }
    // The earlier attempt may have been answered after all (the server finishes without us): look first.
    // The pending question is the last one on screen; it counts even when this attempt was not stored.
    const lastQuestion = messages.filter((m) => m.role === 'user').at(-1);
    const localUsers = countUsers(messages) + (lastQuestion?.unsent ? 1 : 0);
    const gen = genRef.current;
    const live = (): boolean => gen === genRef.current;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    streamingRef.current = true;
    setStreaming(true);
    setError(null);
    const invalidateChartId = knownChartId;
    void (async () => {
      const stored = await loadStored(id, ctrl.signal);
      if (!live()) return;
      if (abortRef.current === ctrl) abortRef.current = null;
      streamingRef.current = false;
      setStreaming(false);
      if (stored && answered(stored, localUsers)) showStored(stored, invalidateChartId);
      else retryPostRef.current(text);
    })();
  };
  retryRef.current = retry;
  retryPostRef.current = (text) => doSend(text, true);

  const reloadAfterTopUp = async (): Promise<void> => {
    const wasAsked = needTopUpRef.current;
    setNeedTopUp(false);
    if (!wasAsked || !pendingRef.current || goneRef.current || waitingRef.current) return;
    const gen = genRef.current;
    const live = (): boolean => gen === genRef.current;
    waitingRef.current = true;
    setWaitingForStars(true);
    const before = await (balanceBeforeRef.current ?? Promise.resolve(null));
    const started = Date.now();
    for (;;) {
      if (!live()) return;
      const now = await fetchBalance();
      if (!live()) return;
      if (course ? now !== null && now >= (course.preview?.priceStars ?? Infinity) : before !== null && now !== null && now > before) break;
      if (Date.now() - started >= STAR_CREDIT_WAIT_MS) {
        if (course) {
          waitingRef.current = false; setWaitingForStars(false); setNeedTopUp(true);
          return;
        }
        break;
      }
      await sleep(STAR_CREDIT_POLL_MS);
    }
    if (!live()) return;
    waitingRef.current = false;
    setWaitingForStars(false);
    takenBackRef.current = false;
    balanceBeforeRef.current = null;
    void qc.invalidateQueries({ queryKey: ['star-wallet'] });
    retryRef.current();
  };

  /** Reload/history entry: reconcile the saved request without initiating a paid POST. */
  const resume = (): boolean => {
    if (!course || !pendingRef.current || !convIdRef.current) return false;
    if (streamingRef.current || waitingRef.current) return true;
    const id = convIdRef.current;
    const gen = genRef.current;
    const live = (): boolean => gen === genRef.current;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    streamingRef.current = true; setStreaming(true); setError(null);
    void (async () => {
      const stored = await waitForStoredAnswer(id, 0, ctrl.signal, live);
      if (!live()) return;
      if (stored) showStored(stored, null);
      else { lastErrorRef.current = 'network'; setError('network'); }
      if (abortRef.current === ctrl) abortRef.current = null;
      streamingRef.current = false; setStreaming(false);
    })();
    return true;
  };

  function open(): void {
    if (!course?.preview?.enabled || !jwt || streamingRef.current || loadState !== 'ready') return;
    openingFailedRef.current = false;
    streamingRef.current = true;
    setStreaming(true);
    setError(null);
    const gen = genRef.current;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void streamChat({
      base: API_BASE, path: '/api/v1/course/chat/open', jwt, signal: ctrl.signal,
      body: { target: course.preview.target, version: course.preview.version, expectedPriceStars: course.preview.priceStars, requestId: crypto.randomUUID(), lang },
      onConversation: (id) => { if (gen === genRef.current) { convIdRef.current = id; setConversationId(id); } },
      onDelta: () => {},
      onDone: () => {},
      onError: (code) => { if (gen === genRef.current) { openingFailedRef.current = true; setError(code); if (code === 'quote_changed') course.refreshPreview(); } },
    }).finally(() => {
      if (gen !== genRef.current) return;
      if (abortRef.current === ctrl) abortRef.current = null;
      streamingRef.current = false; setStreaming(false);
    });
  }

  return {
    loadState,
    loadError,
    header,
    messages,
    canShowChips: !course && isNew && loadState === 'ready' && !messages.some((m) => m.role === 'user'),
    streaming,
    error,
    needTopUp,
    waitingForStars,
    resume,
    open,
    send: (text) => doSend(text, false),
    retry,
    dismissTopUp: () => {
      setNeedTopUp(false);
      if (!takenBackRef.current) {
        return null;
      }
      takenBackRef.current = false;
      balanceBeforeRef.current = null;
      const text = pendingRef.current;
      clearPending();
      return text;
    },
    reloadAfterTopUp,
    reload: () => void query.refetch(),
    chartId: knownChartId,
    conversationId,
  };
}
